import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import {
  Brain, Send, UploadCloud, FileText, Image as ImageIcon, Trash2, Loader2,
  Sparkles, HardDrive, Archive, X, Network, Mic, MicOff, Paperclip,
  Compass, FileSearch, CalendarClock, Radar, Scale, Zap, ChevronDown, ChevronUp,
  History, Plus, Mail, Pencil,
} from "lucide-react";
import NoamResponse from "@/components/NoamResponse";
import NoamReasoning from "@/components/NoamReasoning";
import AgenticEnvironmentGraph from "@/components/AgenticEnvironmentGraph";
import DriftAlertToast from "@/components/DriftAlertToast";

const MAX_FILES = 30;
const UPLOAD_CONCURRENCY = 3;

/* 6 familles d'intention — routées par l'Intent Router V1 existant */
const INTENT_FAMILY_DEFS = [
  { icon: Compass, k: "comprendre" },
  { icon: FileSearch, k: "analyser" },
  { icon: CalendarClock, k: "preparer" },
  { icon: Radar, k: "surveiller" },
  { icon: Scale, k: "decider" },
  { icon: Zap, k: "agir" },
];

/* Instruments réellement mobilisés (modules_used) → types de nœuds du monde connecté */
const MODULE_TO_NODE = {
  annuaire: "entity", risques: "risk", grc: "risk", courtage: "mandate",
  negociations: "mandate", origination: "opp", radar: "signal", feed: "signal", chainage: "chain",
};

function bytesToHuman(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function IntelligenceV2() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = (i18n.language || "fr").slice(0, 2);
  const accountFirst = (user?.name || "").trim().split(/\s+/)[0] || "";
  // Prénom du profil NOAM prioritaire ; nom de compte générique ignoré
  const [profileFirstName, setProfileFirstName] = useState("");
  useEffect(() => {
    api.get("/workspace/intelligence/v2/profile")
      .then((r) => setProfileFirstName((r.data?.first_name || "").trim()))
      .catch(() => {});
  }, []);
  const firstName = profileFirstName
    || (/^(admin|administrator|user|test)$/i.test(accountFirst) ? "" : accountFirst);

  // Session persistante en localStorage (TTL 23h, aligné sur le TTL backend 24h).
  const sessionId = useRef(null);
  if (sessionId.current === null) {
    sessionId.current = (() => {
      try {
        const stored = localStorage.getItem("noam_intel_v2_session");
        if (stored) {
          const parsed = JSON.parse(stored);
          const ageHours = (Date.now() - (parsed?.created_at || 0)) / 3600000;
          if (parsed?.id && ageHours < 23) return parsed.id;
        }
      } catch { /* ignore corrupted storage */ }
      const fresh = `int-v2-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
      try {
        localStorage.setItem("noam_intel_v2_session", JSON.stringify({ id: fresh, created_at: Date.now() }));
      } catch { /* ignore quota errors */ }
      return fresh;
    })();
  }
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, current: "" });
  const [dragOver, setDragOver] = useState(false);
  const [contextSummary, setContextSummary] = useState({ modules_present: [], stats: {}, files_count: 0 });
  const [shortcuts, setShortcuts] = useState([]);
  const [recognitionSupported, setRecognitionSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [coach, setCoach] = useState("copilote_ia");
  const [availableAgents, setAvailableAgents] = useState([]);
  const [activeFamily, setActiveFamily] = useState(null);
  const [showWorld, setShowWorld] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  const endRef = useRef(null);
  // Scroll libre pendant le streaming : on ne colle au bas QUE si l'utilisateur
  // y est déjà — remonter pour relire n'est plus interrompu par la génération.
  const scrollBoxRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const onMessagesScroll = () => {
    const el = scrollBoxRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // Mémoire de conversations 2026-09 (type GPT/Claude) + mode projet (ampleur)
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [convOpen, setConvOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convQuery, setConvQuery] = useState("");
  const convSearchTimer = useRef(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const saveRename = async () => {
    const id = renamingId;
    const title = renameValue.trim();
    setRenamingId(null);
    if (!id || !title) return;
    try {
      await api.patch(`/workspace/intelligence/v2/conversations/${id}`, { title });
      setConversations((arr) => arr.map((c) => (c.id === id ? { ...c, title } : c)));
    } catch (e) { notify.error(fmtError(e, "Renommage impossible.")); }
  };
  const [ecoScope, setEcoScope] = useState("auto");
  // Grappe depuis email 2026-09
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailText, setEmailText] = useState("");

  const sendEmail = () => {
    const content = emailText.trim();
    if (!content) return;
    setEmailOpen(false);
    setEmailText("");
    send(
      `Voici un email que je te transmets. Structure son contenu en écosystème complet (entités, relations, risques, plan, opportunités) et propose-moi la grappe à valider :\n\n"""\n${content}\n"""`,
      null,
      "complete"
    );
  };

  const loadConversations = useCallback(async (q = "") => {
    try {
      const r = await api.get("/workspace/intelligence/v2/conversations", { params: q ? { q } : {} });
      setConversations(r.data?.items || []);
    } catch { setConversations([]); }
  }, []);

  const onConvSearch = (val) => {
    setConvQuery(val);
    if (convSearchTimer.current) clearTimeout(convSearchTimer.current);
    convSearchTimer.current = setTimeout(() => loadConversations(val.trim()), 300);
  };

  const switchSession = (id) => {
    sessionId.current = id;
    try {
      localStorage.setItem("noam_intel_v2_session", JSON.stringify({ id, created_at: Date.now() }));
    } catch { /* ignore quota errors */ }
    setMessages([]);
    setFiles([]);
    setInput("");
    setConvOpen(false);
    setSessionEpoch((e) => e + 1);
  };

  const newConversation = () => switchSession(`int-v2-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`);

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/workspace/intelligence/v2/conversations/${id}`);
      notify.success("Conversation supprimée.");
      if (id === sessionId.current) newConversation();
      loadConversations(convQuery.trim());
    } catch (e) { notify.error(fmtError(e, "Suppression impossible.")); }
  };

  // AGENTIC V1 — intention transmise (?intent=...) + fichiers de la barre globale
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("intent");
    const pending = window.__noamPendingFiles;
    if (pending && pending.length) {
      window.__noamPendingFiles = null;
      window.__noamIntentConsumed = true;
      Promise.resolve(uploadFiles(pending))
        .then(() => api.post(`/workspace/intelligence/v2/sessions/${sessionId.current}/save-to-drive`, { file_ids: [] }))
        .then(() => (q ? send(q) : null))
        .then(() => api.post("/workspace/agentic/track", { event: "first_value", meta: { surface: "global_bar_file" } }))
        .catch(() => {});
      return;
    }
    if (q && !window.__noamIntentConsumed) {
      window.__noamIntentConsumed = true;
      Promise.resolve(send(q))
        .then(() => api.post("/workspace/agentic/track", { event: "first_value", meta: { surface: "intelligence_v2_intent" } }))
        .catch(() => api.post("/workspace/agentic/track", { event: "failure", meta: { surface: "intelligence_v2_intent" } }).catch(() => {}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === Web Speech API — entrée vocale ===
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setRecognitionSupported(false); return; }
    setRecognitionSupported(true);
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.lang = lang === "en" ? "en-US" : lang === "de" ? "de-DE" : "fr-FR";
    rec.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) transcript += event.results[i][0].transcript;
      setInput(transcript);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => { try { rec.abort(); } catch { /* ignore */ } };
  }, [lang]);

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    if (listening) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      setListening(false);
    } else {
      try { setInput(""); recognitionRef.current.start(); setListening(true); }
      catch { setListening(false); }
    }
  };

  // Restauration de l'historique OU brief pro-actif (Noam parle en premier)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/workspace/intelligence/v2/sessions/${sessionId.current}/messages`);
        if (cancelled) return;
        const items = Array.isArray(r.data?.items) ? r.data.items : [];
        if (items.length === 0) {
          const greet = { id: "greet", role: "assistant", text: t("saas.intelligence.v2_greeting") };
          setMessages([greet]);
          try {
            const br = await api.get(`/workspace/intelligence/v2/proactive-brief?language=${lang}`);
            if (cancelled) return;
            const briefText = (br.data?.text || "").trim();
            const briefActions = Array.isArray(br.data?.actions) ? br.data.actions : [];
            if (briefText || briefActions.length > 0) {
              setMessages([greet, { id: "proactive-brief", role: "assistant", text: briefText || "", actions: briefActions, proactive: true }]);
            }
            api.post("/workspace/intelligence/v2/proactive-brief/mark-seen").catch(() => {});
          } catch { /* brief indispo */ }
        } else {
          setMessages(items.map((m) => ({ id: m.id, role: m.role, text: m.text || "", modules_used: m.modules_used, actions: m.actions || [], context_objects: m.context_objects || [], web_sources: m.web_sources || [] })));
          api.post("/workspace/intelligence/v2/proactive-brief/mark-seen").catch(() => {});
        }
      } catch {
        if (!cancelled) setMessages([{ id: "greet", role: "assistant", text: t("saas.intelligence.v2_greeting") }]);
      }
    })();
    return () => { cancelled = true; };
  }, [lang, sessionEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stickToBottomRef.current) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  useEffect(() => {
    api.get(`/workspace/intelligence/v2/shortcuts?language=${lang}`)
      .then((r) => setShortcuts(r.data?.items || []))
      .catch(() => setShortcuts([]));
  }, [lang]);

  // Agents disponibles → sélecteur de coach discret
  useEffect(() => {
    api.get("/workspace/agents")
      .then((r) => {
        const chatAgents = (r.data?.items || []).filter(
          (a) => ["copilote_ia", "coach_cro", "coach_investment", "coach_compliance"].includes(a.slug) && a.active && a.status === "implemented"
        );
        setAvailableAgents(chatAgents);
        if (!chatAgents.find((a) => a.slug === coach)) {
          const fallback = chatAgents.find((a) => a.slug === "copilote_ia") || chatAgents[0];
          if (fallback) setCoach(fallback.slug);
        }
      })
      .catch(() => setAvailableAgents([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshContext = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/intelligence/v2/sessions/${sessionId.current}/context-summary`);
      setContextSummary(r.data || { modules_present: [], stats: {}, files_count: 0 });
      setFiles(r.data?.files || []);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionEpoch]);

  useEffect(() => { refreshContext(); }, [refreshContext]);

  // Upload files — par paquets pour éviter le timeout k8s 60s
  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const all = Array.from(fileList);
    const validFiles = [];
    for (const f of all) {
      if (f.size > 12 * 1024 * 1024) {
        notify.error(`${f.name} : ${t("saas.intelligence.v2_file_rejected_size")}`);
        continue;
      }
      validFiles.push(f);
    }
    if (validFiles.length === 0) return;
    if (files.length + validFiles.length > MAX_FILES) {
      notify.error(t("saas.intelligence.v2_file_rejected_limit"));
      validFiles.splice(MAX_FILES - files.length);
      if (validFiles.length === 0) return;
    }
    setUploading(true);
    setDocsOpen(true);
    setUploadProgress({ done: 0, total: validFiles.length, current: validFiles[0]?.name || "" });
    let okCount = 0;
    let failCount = 0;
    const pending = [...validFiles];
    const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, pending.length) }, async () => {
      while (pending.length > 0) {
        const file = pending.shift();
        if (!file) break;
        setUploadProgress((p) => ({ ...p, current: file.name }));
        try {
          const fd = new FormData();
          fd.append("files", file);
          const r = await api.post(`/workspace/intelligence/v2/sessions/${sessionId.current}/upload`, fd,
            { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
          const accepted = r.data?.accepted || [];
          const rejected = r.data?.rejected || [];
          if (accepted.length > 0) okCount += 1;
          accepted.filter((a) => a.truncated).forEach((a) => {
            notify.info(
              `${a.filename} : document volumineux — un résumé couvrant 100% du contenu est généré automatiquement pour l'analyse.`,
              { duration: 8000 }
            );
          });
          if (rejected.length > 0) {
            failCount += 1;
            rejected.forEach((rj) => notify.error(`${rj.filename} : ${rj.reason}`));
          }
        } catch (e) {
          failCount += 1;
          notify.error(t("saas.axp2.err_upload", { name: file.name, code: e?.response?.status || "network" }));
        } finally {
          setUploadProgress((p) => ({ ...p, done: p.done + 1 }));
        }
      }
    });
    try {
      await Promise.all(workers);
      if (okCount > 0) notify.success(t("saas.intelligence.v2_files_loaded", { count: okCount }));
      if (failCount > 0) notify.error(`${failCount} fichier(s) ont échoué`);
      await refreshContext();
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0, current: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = async (fileId) => {
    try {
      await api.delete(`/workspace/intelligence/v2/sessions/${sessionId.current}/files/${fileId}`);
      await refreshContext();
    } catch (e) { notify.error(fmtError(e, "Delete failed")); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  // Send message — pattern async (202 + polling) pour bypass timeout k8s/Cloudflare
  const send = async (text, shortcutKey = null, scopeOverride = null) => {
    const msg = (text ?? input).trim();
    if (!msg && !shortcutKey) return;
    const displayText = shortcutKey ? (shortcuts.find((s) => s.key === shortcutKey)?.label || msg || "") : msg;
    const tempId = `u-${Date.now()}`;
    stickToBottomRef.current = true;
    setMessages((m) => [...m, { id: tempId, role: "user", text: displayText }]);
    setInput("");
    setActiveFamily(null);
    setBusy(true);
    try {
      const startRes = await api.post(`/workspace/intelligence/v2/chat/start`, {
        session_id: sessionId.current, message: msg, language: lang,
        shortcut: shortcutKey, include_files: true, coach,
        ecosystem_scope: scopeOverride || ecoScope,
      });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("no job_id");
      // Poll toutes les 1.2s (streaming : partial_text affiché au fil de l'eau),
      // max 180s. Tolérance aux erreurs transitoires (502 Cloudflare) : 5 max.
      let finalRes = null;
      let lastProgress = "";
      let pollFails = 0;
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 1200));
        let poll;
        try {
          poll = await api.get(`/workspace/intelligence/v2/chat/jobs/${jobId}`);
          pollFails = 0;
        } catch (pe) {
          pollFails += 1;
          if (pollFails >= 5) throw pe;
          continue;
        }
        const st = poll.data?.status;
        const prog = poll.data?.progress || "";
        const partial = poll.data?.partial_text || "";
        if (partial) {
          // Streaming réel : le texte partiel remplace l'indicateur de raisonnement
          setMessages((m) => {
            const arr = m.filter((x) => x.role !== "thinking");
            const idx = arr.findIndex((x) => x.id === `stream-${jobId}`);
            if (idx >= 0) {
              arr[idx] = { ...arr[idx], text: partial };
              return [...arr];
            }
            return [...arr, { id: `stream-${jobId}`, role: "streaming", text: partial }];
          });
        } else if (prog && prog !== lastProgress) {
          lastProgress = prog;
          setMessages((m) => {
            const arr = [...m];
            const lastIdx = arr.length - 1;
            if (lastIdx >= 0 && arr[lastIdx].role === "user") {
              return [...arr, { id: `thinking-${jobId}`, role: "thinking", text: prog }];
            }
            if (lastIdx >= 0 && arr[lastIdx].role === "thinking") {
              arr[lastIdx] = { ...arr[lastIdx], text: prog };
            }
            return arr;
          });
        }
        if (st === "done") { finalRes = poll.data; break; }
        if (st === "failed") throw new Error(poll.data?.error || "job failed");
      }
      setMessages((m) => m.filter((x) => x.role !== "thinking" && x.role !== "streaming"));
      if (!finalRes) throw new Error("polling timeout (>3min)");
      setMessages((m) => [...m, {
        id: finalRes.assistant_message_id || `a-${Date.now()}`,
        role: "assistant", text: finalRes.reply || "",
        modules_used: finalRes.modules_used, actions: finalRes.actions || [],
        context_objects: finalRes.context_objects || [],
        web_sources: finalRes.web_sources || [],
      }]);
    } catch (e) {
      setMessages((m) => m.filter((x) => x.role !== "thinking" && x.role !== "streaming"));
      // Récupération : la réponse a peut-être été persistée côté serveur malgré l'erreur réseau
      try {
        const rec = await api.get(`/workspace/intelligence/v2/sessions/${sessionId.current}/messages`);
        const items = Array.isArray(rec.data?.items) ? rec.data.items : [];
        const last = items[items.length - 1];
        const prev = items[items.length - 2];
        if (last?.role === "assistant" && prev?.role === "user" && (prev.text || "").trim() === displayText.trim()) {
          setMessages(items.map((m) => ({ id: m.id, role: m.role, text: m.text || "", modules_used: m.modules_used, actions: m.actions || [], context_objects: m.context_objects || [], web_sources: m.web_sources || [] })));
          return;
        }
      } catch { /* récupération impossible */ }
      const detail = e?.response?.data?.detail || e?.message || "";
      const status = e?.response?.status;
      let humanMsg = t("saas.intelligence.v2_error");
      if (status === 402 || /quota|credit|balance/i.test(detail)) {
        humanMsg = t("saas.axp2.err_credit");
      } else if (status === 429) {
        humanMsg = t("saas.axp2.err_ratelimit");
      } else if (status === 503 || /unavailable|timeout/i.test(detail)) {
        humanMsg = t("saas.axp2.err_ai_down");
      } else if (detail) {
        humanMsg = `Réponse impossible : ${String(detail).slice(0, 240)}`;
      }
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", text: humanMsg }]);
    } finally {
      setBusy(false);
    }
  };

  // Suggestion d'intention → Intent Router existant (aucun second moteur)
  const routeSuggestion = async (textIntent) => {
    if (busy) return;
    try {
      const r = await api.post("/workspace/agentic/intent", { text: textIntent });
      const action = (r.data.actions || [])[0];
      if (action?.type === "navigate") { navigate(action.to); return; }
      send(action?.prefill || textIntent);
    } catch { send(textIntent); }
  };

  const archiveAsBrief = async (msg) => {
    if (!msg.id || !msg.text) return;
    try {
      await api.post(`/workspace/intelligence/v2/messages/${msg.id}/archive-as-brief`, {
        title: msg.text.split("\n")[0].replace(/[#*]/g, "").trim().slice(0, 240),
      });
      notify.success(t("saas.intelligence.v2_exported"));
    } catch (e) { notify.error(fmtError(e, "Archive failed")); }
  };

  const saveAllToDrive = async () => {
    try {
      const r = await api.post(`/workspace/intelligence/v2/sessions/${sessionId.current}/save-to-drive`, { file_ids: [] });
      notify.success(t("saas.intelligence.v2_saved_drive", { count: r.data?.count || 0 }));
    } catch (e) { notify.error(fmtError(e, "Save failed")); }
  };

  const clearSession = async () => {
    try {
      await api.post(`/workspace/intelligence/v2/sessions/${sessionId.current}/clear`, {});
      const fresh = `int-v2-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
      sessionId.current = fresh;
      try {
        localStorage.setItem("noam_intel_v2_session", JSON.stringify({ id: fresh, created_at: Date.now() }));
      } catch { /* ignore quota errors */ }
      setMessages([{ id: "greet", role: "assistant", text: t("saas.intelligence.v2_greeting") }]);
      setFiles([]);
      setSessionEpoch((e) => e + 1);
    } catch (e) { notify.error(fmtError(e, "Clear failed")); }
  };

  const hasUserMsg = messages.some((m) => m.role === "user");
  const hasThinking = messages.some((m) => m.role === "thinking" || m.role === "streaming");
  const modulesActive = contextSummary.modules_present || [];

  // Instruments réellement mobilisés par la dernière réponse → mise en évidence du monde connecté
  const highlightTypes = useMemo(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && m.modules_used?.length);
    if (!lastAssistant) return [];
    return [...new Set((lastAssistant.modules_used || []).map((m) => MODULE_TO_NODE[m]).filter(Boolean))];
  }, [messages]);

  const worldPanel = (
    <div className="space-y-4">
      <AgenticEnvironmentGraph
        heightClass="h-[300px]"
        highlightTypes={highlightTypes}
        onTalk={(intentText) => { setShowWorld(false); send(intentText); }}
        subtitle={highlightTypes.length > 0
          ? t("saas.axp2.highlight_sub")
          : null}
      />

      {/* Documents de session — progressive disclosure */}
      <div className="noam-card" data-testid="v2-files-list">
        <button onClick={() => setDocsOpen(!docsOpen)} aria-expanded={docsOpen}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          data-testid="v2-docs-toggle">
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold inline-flex items-center gap-1.5">
            <FileText className="w-3 h-3 text-[#378ADD]" />
            Documents de session {files.length > 0 && <span className="text-[#378ADD]">· {files.length}</span>}
          </span>
          {docsOpen ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
        </button>
        {docsOpen && (
          <div className="px-4 pb-4 space-y-2">
            <div
              className={`rounded-lg border border-dashed px-3 py-3 text-center transition-colors ${dragOver ? "border-[#378ADD] bg-[#378ADD]/5" : "border-white/10"}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="v2-dropzone"
            >
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading || files.length >= MAX_FILES}
                className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                data-testid="v2-file-pick-btn">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
                {t("saas.intelligence.v2_drop_zone")}
              </button>
              {uploading && uploadProgress.total > 0 && (
                <div className="mt-2 space-y-1" data-testid="v2-upload-progress">
                  <div className="text-[10px] text-zinc-400 flex items-center justify-between">
                    <span className="truncate flex-1 mr-2 text-zinc-300">{uploadProgress.current || "…"}</span>
                    <span>{uploadProgress.done}/{uploadProgress.total}</span>
                  </div>
                  <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#378ADD] transition-transform origin-left"
                      style={{ width: `${Math.round((uploadProgress.done / Math.max(1, uploadProgress.total)) * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
            {files.length > 0 && (
              <>
                <div className="space-y-1.5">
                  {files.map((f) => {
                    const Icon = f.kind === "image" ? ImageIcon : FileText;
                    return (
                      <div key={f.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/5 text-xs"
                        data-testid={`v2-file-${f.id}`}>
                        <Icon className="w-3.5 h-3.5 text-[#378ADD] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-zinc-200">{f.filename}</div>
                          <div className="text-[10px] text-zinc-500">{bytesToHuman(f.size)} · {f.kind}</div>
                        </div>
                        <button onClick={() => removeFile(f.id)} className="text-zinc-500 hover:text-[#E24B4A] p-1 shrink-0"
                          data-testid={`v2-file-remove-${f.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button onClick={saveAllToDrive}
                  className="text-[10px] uppercase tracking-wide text-[#378ADD] hover:text-white inline-flex items-center gap-1 transition-colors"
                  data-testid="v2-save-to-drive-btn">
                  <HardDrive className="w-3 h-3" /> {t("saas.intelligence.v2_save_to_drive")}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Contexte mobilisé */}
      {modulesActive.length > 0 && (
        <div className="noam-card px-4 py-3" data-testid="v2-context-active">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold inline-flex items-center gap-1.5 mb-2">
            <Network className="w-3 h-3 text-[#EF9F27]" /> Contexte mobilisé
          </div>
          <div className="flex flex-wrap gap-1.5">
            {modulesActive.map((m) => (
              <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-[#378ADD]/10 text-[#378ADD] border border-[#378ADD]/20"
                data-testid={`v2-module-${m}`}>
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      <button onClick={clearSession}
        className="w-full text-[11px] text-zinc-600 hover:text-[#E24B4A] inline-flex items-center justify-center gap-1.5 py-1 transition-colors"
        data-testid="v2-clear-session-btn">
        <Trash2 className="w-3 h-3" /> {t("saas.intelligence.v2_clear_session")}
      </button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto" data-testid="intelligence-v2-root">
        {/* ─── En-tête : overline + conversations + coach ───────────────── */}
      <div className="flex items-center justify-between mb-1">
        <div className="noam-overline inline-flex items-center gap-2">
          <Brain className="w-3 h-3 text-[#378ADD]" />
          {t("saas.intelligence.v2_overline")}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => { setConvOpen((o) => !o); if (!convOpen) loadConversations(); }}
            className={`text-[11px] inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 transition-colors ${
              convOpen ? "border-[#378ADD]/60 text-white bg-[#378ADD]/10" : "border-white/10 text-zinc-400 hover:text-zinc-200"
            }`}
            data-testid="v2-conversations-btn"
          >
            <History className="w-3 h-3" /> Conversations
          </button>
          <button
            type="button"
            onClick={newConversation}
            title="Nouvelle conversation"
            className="text-[11px] inline-flex items-center gap-1 border border-white/10 rounded-full px-2.5 py-1 text-zinc-400 hover:text-zinc-200 transition-colors"
            data-testid="v2-new-conversation-btn"
          >
            <Plus className="w-3 h-3" /> Nouvelle
          </button>
          {availableAgents.length > 1 && (
          <select value={coach} onChange={(e) => setCoach(e.target.value)}
            className="text-[11px] bg-transparent border border-white/10 rounded-full px-2.5 py-1 text-zinc-400 hover:text-zinc-200 focus:outline-none focus:border-[#378ADD] transition-colors"
            data-testid="v2-coach-selector" title={t("saas.axp2.coach_title")}>
            {availableAgents.map((a) => (
              <option key={a.slug} value={a.slug} className="bg-zinc-900 text-zinc-100">
                {a.slug === "copilote_ia" ? "Copilote IA" : a.name}
              </option>
            ))}
          </select>
          )}
        </div>
      </div>

      {/* ─── Panneau conversations (mémoire type GPT/Claude) ──────────── */}
      {convOpen && (
        <div className="noam-card p-3 mb-3 max-h-80 overflow-y-auto" data-testid="v2-conversations-panel">
          <input
            value={convQuery}
            onChange={(e) => onConvSearch(e.target.value)}
            placeholder="Rechercher dans les conversations (titres et contenus)…"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-[12px] text-white focus:border-[#378ADD] focus:outline-none mb-2"
            data-testid="v2-conversations-search"
          />
          {conversations.length === 0 ? (
            <div className="text-[12px] text-zinc-500 italic px-1 py-2" data-testid="v2-conversations-empty">
              {convQuery.trim()
                ? `Aucune conversation ne contient « ${convQuery.trim()} ».`
                : "Aucune conversation enregistrée pour l'instant. Chaque échange avec NOAM est conservé ici jusqu'à ce que vous le supprimiez."}
            </div>
          ) : (
            <ul className="space-y-1">
              {conversations.map((c) => (
                <li key={c.id}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-md border transition-colors ${
                    c.id === sessionId.current ? "border-[#378ADD]/50 bg-[#378ADD]/10" : "border-transparent hover:bg-white/[0.04]"
                  }`}
                  data-testid={`v2-conversation-${c.id}`}>
                  <button type="button" onClick={() => switchSession(c.id)} className="flex-1 min-w-0 text-left">
                    {renamingId === c.id ? (
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenamingId(null); }}
                        onBlur={saveRename}
                        autoFocus
                        className="w-full bg-black/40 border border-[#378ADD]/50 rounded px-2 py-1 text-[12px] text-white focus:outline-none"
                        data-testid={`v2-conversation-rename-input-${c.id}`}
                      />
                    ) : (
                      <div className="text-[12px] text-zinc-100 truncate">{c.title || "Conversation"}</div>
                    )}
                    <div className="text-[10px] text-zinc-500">
                      {c.updated_at ? new Date(c.updated_at).toLocaleString("fr-CH") : ""}
                      {typeof c.message_count === "number" ? ` · ${c.message_count} message(s)` : ""}
                      {c.id === sessionId.current ? " · en cours" : ""}
                    </div>
                  </button>
                  <button type="button" title="Renommer la conversation"
                    onClick={(e) => { e.stopPropagation(); setRenamingId(c.id); setRenameValue(c.title || ""); }}
                    className="p-1.5 rounded text-zinc-500 hover:text-[#378ADD] hover:bg-white/5 shrink-0"
                    data-testid={`v2-conversation-rename-${c.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => deleteConversation(c.id)} title="Supprimer la conversation"
                    className="p-1.5 rounded text-zinc-500 hover:text-[#E24B4A] hover:bg-white/5 shrink-0"
                    data-testid={`v2-conversation-delete-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ─── Hero — se replie dès que la conversation commence ────────── */}
      {!hasUserMsg && (
        <div className="mt-4 mb-6" data-testid="v2-hero">
          <h1 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Bonjour{firstName ? ` ${firstName}` : ""}.
          </h1>
          <p className="text-base text-zinc-400 mt-1.5">Que voulez-vous accomplir ?</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* ─── Conversation ──────────────────────────────────────────── */}
        <div className="lg:col-span-8 flex flex-col noam-card overflow-hidden"
          style={{ height: "min(680px, calc(100vh - 210px))" }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDrop={onDrop}
          data-testid="v2-chat">
          <div ref={scrollBoxRef} onScroll={onMessagesScroll} className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5" data-testid="v2-messages">
            <DriftAlertToast sessionId={sessionId.current} onAskRecompute={(prompt) => { setInput(prompt); send(prompt); }} />
            {messages.map((m) => {
              if (m.role === "thinking") {
                return <NoamReasoning key={m.id} progress={m.text} modulesCount={modulesActive.length} />;
              }
              if (m.role === "streaming") {
                // Streaming réel : texte partiel affiché au fil de la génération
                return (
                  <div key={m.id} className="flex justify-start">
                    <div className="max-w-[92%] sm:max-w-[85%]" data-testid="v2-msg-streaming">
                      <div className="rounded-2xl rounded-bl-md border border-white/[0.07] bg-[#111113] px-5 py-4">
                        <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">
                          {m.text}
                          <span className="inline-block w-2 h-4 ml-0.5 bg-[#378ADD] animate-pulse align-middle rounded-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              if (m.role === "user") {
                return (
                  <div key={m.id} className="flex justify-end">
                    <div className="max-w-[88%] sm:max-w-[75%] px-4 py-2.5 rounded-2xl rounded-br-md text-sm whitespace-pre-wrap bg-[#378ADD] text-white"
                      data-testid={`v2-msg-user-${m.id}`}>
                      {m.text}
                    </div>
                  </div>
                );
              }
              return (
                <div key={m.id} className="flex justify-start">
                  <NoamResponse msg={m} onArchive={archiveAsBrief} archiveLabel={t("saas.intelligence.v2_export_brief")} />
                </div>
              );
            })}
            {busy && !hasThinking && <NoamReasoning progress={t("saas.intelligence.v2_thinking")} modulesCount={modulesActive.length} />}
            <div ref={endRef} />
          </div>

          {/* ─── Zone d'entrée premium ─────────────────────────────── */}
          <div className="border-t border-white/[0.06] px-3 sm:px-5 py-3.5 space-y-3 bg-[#0D0D0F]">
            {!hasUserMsg && (
              <div data-testid="v2-intent-families">
                <div className="flex flex-wrap gap-2">
                  {INTENT_FAMILY_DEFS.map((f) => (
                    <button key={f.k}
                      onClick={() => setActiveFamily(activeFamily === f.k ? null : f.k)}
                      data-testid={`v2-intent-${f.k}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                        activeFamily === f.k
                          ? "border-[#378ADD]/60 bg-[#378ADD]/10 text-white"
                          : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-[#378ADD]/40 hover:text-white"
                      }`}>
                      <f.icon className="w-3.5 h-3.5 text-[#378ADD]" /> {t(`saas.axp2.fam_${f.k}`)}
                    </button>
                  ))}
                </div>
                {activeFamily && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="v2-intent-suggestions">
                    {[t(`saas.axp2.sg_${activeFamily}_1`), t(`saas.axp2.sg_${activeFamily}_2`)].map((sug) => (
                      <button key={sug} onClick={() => routeSuggestion(sug)} disabled={busy}
                        className="text-[11px] px-2.5 py-1 rounded-lg border border-white/[0.07] bg-white/[0.02] text-zinc-400 hover:text-white hover:border-white/20 transition-colors">
                        « {sug} »
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {hasUserMsg && shortcuts.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="v2-shortcuts">
                {shortcuts.map((s) => (
                  <button key={s.key} onClick={() => send("", s.key)}
                    className="noam-pill text-[11px] inline-flex items-center gap-1"
                    data-testid={`v2-shortcut-${s.key}`} title={s.description} disabled={busy}>
                    <Sparkles className="w-3 h-3" /> {s.label}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }}
              className={`flex items-center gap-1.5 rounded-2xl border bg-[#141417] px-3 py-1 transition-colors ${
                listening ? "border-[#E24B4A]/50" : "border-white/10 focus-within:border-[#378ADD]/60"
              }`}>
              <select
                value={ecoScope}
                onChange={(e) => setEcoScope(e.target.value)}
                title="Ampleur de l'écosystème que NOAM propose de créer (mode projet)"
                className="text-[10px] bg-transparent border border-white/10 rounded-full px-1.5 py-1 text-zinc-500 hover:text-zinc-300 focus:outline-none focus:border-[#7F77DD] transition-colors shrink-0"
                data-testid="v2-scope-selector"
              >
                <option value="auto" className="bg-zinc-900 text-zinc-100">Grappe : Auto</option>
                <option value="minimal" className="bg-zinc-900 text-zinc-100">Grappe : Minimale</option>
                <option value="median" className="bg-zinc-900 text-zinc-100">Grappe : Médiane</option>
                <option value="complete" className="bg-zinc-900 text-zinc-100">Grappe : Complète</option>
              </select>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={listening ? t("saas.intelligence.v2_listening", { defaultValue: "Écoute… parlez maintenant" }) : "Parler à NOAM…"}
                className="flex-1 bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-zinc-600 min-w-0"
                disabled={busy}
                data-testid="v2-input"
              />
              <input ref={fileInputRef} type="file" multiple onChange={(e) => uploadFiles(e.target.files)}
                className="hidden" accept=".pdf,.docx,.xlsx,.csv,.pptx,.txt,.md,.png,.jpg,.jpeg,.webp,.gif"
                data-testid="v2-file-input" />
              <button type="button" onClick={() => setEmailOpen(true)} disabled={busy}
                title="Coller un email — NOAM le structure en écosystème complet"
                className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-40 transition-colors"
                data-testid="v2-email-btn">
                <Mail className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || files.length >= MAX_FILES}
                title="Joindre un document"
                className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 disabled:opacity-40 transition-colors"
                data-testid="v2-attach-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </button>
              {recognitionSupported && (
                <button type="button" onClick={toggleVoice} disabled={busy}
                  title={listening ? t("saas.axp.mic_stop") : t("saas.axp2.talk_noam")}
                  className={`p-2 rounded-full transition-colors ${listening ? "bg-[#E24B4A]/15 text-[#E24B4A]" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}
                  data-testid="v2-voice-btn">
                  {listening ? <MicOff className="w-4 h-4 animate-pulse" /> : <Mic className="w-4 h-4" />}
                </button>
              )}
              <button type="submit" disabled={busy || !input.trim()}
                className="p-2.5 rounded-full bg-[#378ADD] text-white hover:bg-[#4FA0F2] disabled:opacity-30 transition-colors"
                data-testid="v2-send">
                <Send className="w-4 h-4" />
              </button>
            </form>

            {/* Grappe depuis email 2026-09 */}
            {emailOpen && (
              <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
                onClick={() => setEmailOpen(false)} data-testid="v2-email-overlay">
                <div className="noam-card w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="v2-email-modal">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="noam-overline text-[#7F77DD] inline-flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> Coller un email
                      </div>
                      <div className="text-sm text-zinc-300 mt-1">
                        Collez le contenu d'un email : NOAM en extrait les acteurs, relations, risques et actions, puis vous propose la grappe complète à valider.
                      </div>
                    </div>
                    <button type="button" onClick={() => setEmailOpen(false)}
                      className="p-1 rounded hover:bg-white/5 text-zinc-400" data-testid="v2-email-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea value={emailText} onChange={(e) => setEmailText(e.target.value)}
                    rows={10} placeholder={"De : …\nObjet : …\n\nCollez ici le corps de l'email…"}
                    className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#7F77DD] focus:outline-none resize-y"
                    data-testid="v2-email-textarea" autoFocus />
                  <div className="flex items-center justify-end gap-2 mt-3">
                    <button type="button" onClick={() => setEmailOpen(false)}
                      className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5"
                      data-testid="v2-email-cancel">Annuler</button>
                    <button type="button" onClick={sendEmail} disabled={!emailText.trim()}
                      className="text-[12px] px-3 py-1.5 rounded-md bg-[#7F77DD] text-white hover:bg-[#8F88E5] font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="v2-email-submit">
                      <Network className="w-3.5 h-3.5" /> Structurer en écosystème
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── Le monde que NOAM comprend — monté une seule fois ────────── */}
        {isDesktop ? (
          <aside className="lg:col-span-4">{worldPanel}</aside>
        ) : (
          <div>
            <button onClick={() => setShowWorld(!showWorld)} aria-expanded={showWorld}
              className="w-full noam-card px-4 py-3 flex items-center justify-between text-sm text-zinc-300"
              data-testid="v2-world-toggle">
              <span className="inline-flex items-center gap-2">
                <Network className="w-4 h-4 text-[#EFD127]" /> Le monde que NOAM comprend
              </span>
              {showWorld ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
            </button>
            {showWorld && <div className="mt-3">{worldPanel}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
